import * as React from "react";
import { Search, X, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

type SearchAndFilterBarProps = {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  statusFilter: string | undefined;
  onStatusFilterChange: (status: string | undefined) => void;
  categoryFilter: string | undefined;
  onCategoryFilterChange: (category: string | undefined) => void;
  tagsFilter: string[];
  onTagsFilterChange: (tags: string[]) => void;
  availableCategories: string[];
  availableTags: string[];
  resultCount: number;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
};

export function SearchAndFilterBar({
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  categoryFilter,
  onCategoryFilterChange,
  tagsFilter,
  onTagsFilterChange,
  availableCategories,
  availableTags,
  resultCount,
  hasActiveFilters,
  onClearFilters,
}: SearchAndFilterBarProps) {
  const [localSearchQuery, setLocalSearchQuery] = React.useState(searchQuery);

  React.useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearchQuery(value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onSearchChange(localSearchQuery);
    }
  };

  const handleSearchBlur = () => {
    if (localSearchQuery !== searchQuery) {
      onSearchChange(localSearchQuery);
    }
  };

  const handleClearSearch = () => {
    setLocalSearchQuery("");
    onSearchChange("");
  };

  const handleToggleTag = (tag: string) => {
    if (tagsFilter.includes(tag)) {
      onTagsFilterChange(tagsFilter.filter((t) => t !== tag));
    } else {
      onTagsFilterChange([...tagsFilter, tag]);
    }
  };

  const statusOptions = [
    { value: "draft", label: "Draft" },
    { value: "published", label: "Published" },
    { value: "archived", label: "Archived" },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search documents by title, content, tags, or category..."
            value={localSearchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            onBlur={handleSearchBlur}
            className="pl-10 pr-10"
          />
          {localSearchQuery && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="default">
              <Filter className="mr-2 size-4" />
              Filters
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-2 rounded-full px-1.5 py-0.5 text-xs">
                  {[statusFilter, categoryFilter, ...(tagsFilter.length > 0 ? ["tags"] : [])].filter(Boolean).length}
                </Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Status</DropdownMenuLabel>
            {statusOptions.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.value}
                checked={statusFilter === option.value}
                onCheckedChange={(checked) => {
                  onStatusFilterChange(checked ? option.value : undefined);
                }}
              >
                {option.label}
              </DropdownMenuCheckboxItem>
            ))}

            {availableCategories.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Category</DropdownMenuLabel>
                {availableCategories.map((category) => (
                  <DropdownMenuCheckboxItem
                    key={category}
                    checked={categoryFilter === category}
                    onCheckedChange={(checked) => {
                      onCategoryFilterChange(checked ? category : undefined);
                    }}
                  >
                    {category}
                  </DropdownMenuCheckboxItem>
                ))}
              </>
            )}

            {availableTags.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Tags</DropdownMenuLabel>
                {availableTags.slice(0, 10).map((tag) => (
                  <DropdownMenuCheckboxItem
                    key={tag}
                    checked={tagsFilter.includes(tag)}
                    onCheckedChange={() => handleToggleTag(tag)}
                  >
                    {tag}
                  </DropdownMenuCheckboxItem>
                ))}
              </>
            )}

            {hasActiveFilters && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onClearFilters}>
                  <X className="mr-2 size-4" />
                  Clear all filters
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {(searchQuery || hasActiveFilters) && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex flex-wrap items-center gap-2">
            <span>{resultCount} result{resultCount !== 1 ? "s" : ""}</span>
            {searchQuery && (
              <Badge variant="secondary">
                Search: {searchQuery}
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="ml-1 hover:text-foreground"
                >
                  <X className="size-3" />
                </button>
              </Badge>
            )}
            {statusFilter && (
              <Badge variant="secondary">
                Status: {statusOptions.find((o) => o.value === statusFilter)?.label}
                <button
                  type="button"
                  onClick={() => onStatusFilterChange(undefined)}
                  className="ml-1 hover:text-foreground"
                >
                  <X className="size-3" />
                </button>
              </Badge>
            )}
            {categoryFilter && (
              <Badge variant="secondary">
                Category: {categoryFilter}
                <button
                  type="button"
                  onClick={() => onCategoryFilterChange(undefined)}
                  className="ml-1 hover:text-foreground"
                >
                  <X className="size-3" />
                </button>
              </Badge>
            )}
            {tagsFilter.map((tag) => (
              <Badge key={tag} variant="secondary">
                Tag: {tag}
                <button
                  type="button"
                  onClick={() => handleToggleTag(tag)}
                  className="ml-1 hover:text-foreground"
                >
                  <X className="size-3" />
                </button>
              </Badge>
            ))}
          </div>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={onClearFilters}>
              Clear all
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
